import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';

export interface StripeAccount {
  id: string;
  label: string;
  displayName: string;
  publishableKey: string | null;
  stripeAccountId: string | null;
  stripeMode: 'test' | 'live' | null;
  secretKey: {
    set: boolean;
    last4: string | null;
    updatedAt: string | null;
    /** null when the last write came from the break-glass CLI. */
    updatedBy: string | null;
  };
  webhookSecret: {
    set: boolean;
    last4: string | null;
    updatedAt: string | null;
    updatedBy: string | null;
  };
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStripeAccountBody {
  label: string;
  displayName: string;
  secretKey: string;
  webhookSecret: string;
  publishableKey: string;
  stripeAccountId?: string;
}

/**
 * Patch body. Omitting a field leaves it alone (matches the existing
 * /events PATCH convention). Empty string on `stripeAccountId` clears it
 * back to null.
 */
export interface PatchStripeAccountBody {
  label?: string;
  displayName?: string;
  publishableKey?: string;
  stripeAccountId?: string;
  secretKey?: string;
  webhookSecret?: string;
}

export function useStripeAccounts() {
  return useQuery({
    queryKey: ['stripeAccounts'],
    queryFn: () =>
      apiFetch<{ stripeAccounts: StripeAccount[] }>('/api/v1/admin/stripe-accounts').then(
        (r) => r.stripeAccounts,
      ),
  });
}

export function useStripeAccount(id: string) {
  return useQuery({
    queryKey: ['stripeAccount', id],
    queryFn: () =>
      apiFetch<{ stripeAccount: StripeAccount }>(`/api/v1/admin/stripe-accounts/${id}`).then(
        (r) => r.stripeAccount,
      ),
    enabled: Boolean(id),
  });
}

export function useCreateStripeAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateStripeAccountBody) =>
      apiFetch<{ stripeAccount: StripeAccount }>('/api/v1/admin/stripe-accounts', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.stripeAccount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stripeAccounts'] });
    },
  });
}

export function usePatchStripeAccount(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchStripeAccountBody) =>
      apiFetch<{ stripeAccount: StripeAccount }>(`/api/v1/admin/stripe-accounts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.stripeAccount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stripeAccounts'] });
      qc.invalidateQueries({ queryKey: ['stripeAccount', id] });
    },
  });
}
